import React, { useState } from "react";
import MainLayout from "../layout/MainLayout";
import { API_BASE } from "../config/orbisConfig";

const API_URL = "${API_BASE}/directory_entities_import.php";

const DirectoryImportPage = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const authToken = localStorage.getItem("cani_auth") || "";

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0] || null;
        setFile(selectedFile);
        setResult(null);
        setError("");
    };

    const handleImport = async (e) => {
        e.preventDefault();

        if (!file) {
            setError("Selecciona un archivo antes de importar.");
            return;
        }

        setLoading(true);
        setResult(null);
        setError("");

        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(API_URL, {
                method: "POST",
                headers: {
                    "X-CANI-AUTH": authToken,
                },
                body: formData,
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.detail
                        ? `${data.error} ${data.detail}`
                        : (data.error || data.message || "Error al importar archivo.")
                );
            }

            setResult(data);
        } catch (err) {
            setError(err.message || "Ocurrió un error al importar.");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFile(null);
        setResult(null);
        setError("");
        const input = document.getElementById("directory-import-file");
        if (input) input.value = "";
    };

    return (
        <MainLayout>
            <div className="container py-4">
                <div className="card shadow-sm">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <div>
                                <h2 className="mb-1">Importación de Directorio Estratégico</h2>
                                <p className="text-muted mb-0">
                                    Sube un archivo Excel con el formato autorizado para importar entidades.
                                </p>
                            </div>
                        </div>

                        <form onSubmit={handleImport}>
                            <div className="mb-3">
                                <label htmlFor="directory-import-file" className="form-label">
                                    Archivo Excel
                                </label>
                                <input
                                    id="directory-import-file"
                                    type="file"
                                    className="form-control"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileChange}
                                />
                            </div>

                            {file && (
                                <div className="mb-3">
                                    <small className="text-muted">
                                        Archivo seleccionado: <strong>{file.name}</strong>
                                    </small>
                                </div>
                            )}

                            <div className="d-flex gap-2">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? "Importando..." : "Importar archivo"}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleClear}
                                    disabled={loading}
                                >
                                    Limpiar
                                </button>
                            </div>
                        </form>

                        {error && (
                            <div className="alert alert-danger mt-4 mb-0">
                                <strong>Error:</strong> {error}
                            </div>
                        )}

                        {result && (
                            <div className="mt-4">
                                <div className="alert alert-success">
                                    <strong>{result.message || "Importación completada."}</strong>
                                </div>

                                <div className="row g-3 mb-4">
                                    <div className="col-md-4">
                                        <div className="card border-0 bg-light h-100">
                                            <div className="card-body">
                                                <div className="text-muted small mb-1">Procesados</div>
                                                <div className="fs-3 fw-bold">{result.processed ?? 0}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div className="card border-0 bg-light h-100">
                                            <div className="card-body">
                                                <div className="text-muted small mb-1">Insertados</div>
                                                <div className="fs-3 fw-bold">{result.inserted ?? 0}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div className="card border-0 bg-light h-100">
                                            <div className="card-body">
                                                <div className="text-muted small mb-1">Omitidos</div>
                                                <div className="fs-3 fw-bold">{result.skipped ?? 0}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {Array.isArray(result.sheets) && result.sheets.length > 0 && (
                                    <div className="card">
                                        <div className="card-body">
                                            <h5 className="mb-3">Detalle por hoja</h5>

                                            <div className="table-responsive">
                                                <table className="table table-bordered table-hover align-middle mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Hoja</th>
                                                            <th>Procesados</th>
                                                            <th>Insertados</th>
                                                            <th>Omitidos</th>
                                                            <th>Observación</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {result.sheets.map((sheet, index) => (
                                                            <tr key={index}>
                                                                <td>{sheet.sheet || "-"}</td>
                                                                <td>{sheet.processed ?? 0}</td>
                                                                <td>{sheet.inserted ?? 0}</td>
                                                                <td>{sheet.skipped ?? 0}</td>
                                                                <td>{sheet.warning || "-"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-4">
                            <div className="card border-0 bg-light">
                                <div className="card-body">
                                    <h6 className="mb-2">Formato esperado del Excel</h6>
                                    <div className="small text-muted">
                                        A: Nombre entidad<br />
                                        B: Tipo entidad<br />
                                        C: Subcategoría<br />
                                        D: Sector / giro<br />
                                        E: Nombre contacto principal<br />
                                        F: Puesto / rol<br />
                                        G: Teléfono<br />
                                        H: WhatsApp<br />
                                        I: Email<br />
                                        J: Región<br />
                                        K: Ciudad<br />
                                        L: Dirección<br />
                                        M: Sitio web<br />
                                        N: Fuente / origen<br />
                                        O: Prioridad<br />
                                        P: Estatus<br />
                                        Q: Nota 1<br />
                                        R: Nota 2<br />
                                        S: Nota 3
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default DirectoryImportPage;
